import * as THREE from 'three'

import type { AnimationLayer } from '@/types/animationLayer'

/** `THREE.Group`（图层根）上用于屏幕贴图 / 视频的运行时 `userData` 键。 */
export const SCREEN_USERDATA = {
  screenMap: 'screenMap',
  screenVideoEl: 'screenVideoEl',
} as const

/** 用户上传贴图在 `emissiveMap` 上的强度，接近真实设备屏亮度（避免过曝）。 */
export const USER_SCREEN_EMISSIVE_INTENSITY = 0.32

/**
 * 单 slot 上 glTF 原始屏幕材质的可恢复快照（纹理引用 + 颜色拷贝）。
 */
export type ScreenMaterialSlotSnapshot = {
  map: THREE.Texture | null
  emissiveMap: THREE.Texture | null
  color: THREE.Color
  emissive: THREE.Color
  emissiveIntensity: number
  toneMapped: boolean
  roughness: number
  metalness: number
  envMapIntensity: number
}

/**
 * 读取屏幕 mesh 当前各材质槽状态，用于在用户清除自定义贴图时还原 glTF 默认。
 *
 * @param mesh - 屏幕网格
 */
export function snapshotScreenMeshMaterials(mesh: THREE.Mesh): ScreenMaterialSlotSnapshot[] {
  const mats: THREE.Material[] = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
  return mats.map((m) => {
    if (m instanceof THREE.MeshStandardMaterial) {
      return {
        map: m.map,
        emissiveMap: m.emissiveMap,
        color: m.color.clone(),
        emissive: m.emissive.clone(),
        emissiveIntensity: m.emissiveIntensity,
        toneMapped: m.toneMapped,
        roughness: m.roughness,
        metalness: m.metalness,
        envMapIntensity: m.envMapIntensity,
      }
    }
    return {
      map: null,
      emissiveMap: null,
      color: new THREE.Color(0x1a1d22),
      emissive: new THREE.Color(0x000000),
      emissiveIntensity: 1,
      toneMapped: true,
      roughness: 1,
      metalness: 0,
      envMapIntensity: 1,
    }
  })
}

/**
 * 将快照写回屏幕 mesh（与 {@link snapshotScreenMeshMaterials} 成对使用）。
 *
 * @param mesh - 屏幕网格
 * @param snaps - 与材质槽一一对应的快照
 */
export function applySnapshotToScreenMesh(mesh: THREE.Mesh, snaps: ScreenMaterialSlotSnapshot[]): void {
  const mats: THREE.Material[] = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
  snaps.forEach((snap, i) => {
    const m = mats[i]
    if (m instanceof THREE.MeshStandardMaterial) {
      m.map = snap.map
      m.emissiveMap = snap.emissiveMap
      m.color.copy(snap.color)
      m.emissive.copy(snap.emissive)
      m.emissiveIntensity = snap.emissiveIntensity
      m.toneMapped = snap.toneMapped
      m.roughness = snap.roughness
      m.metalness = snap.metalness
      m.envMapIntensity = snap.envMapIntensity
      m.needsUpdate = true
    }
  })
}

/**
 * 将视频 `currentTime` 设为目标值；导出路径在 `seeked` 后再渲染以避免错帧。
 *
 * @param video - 已挂载 `src` 的 `<video>`
 * @param timeSec - 目标时间（秒）
 * @param timeoutMs - 超时后仍 resolve，避免导出死锁
 */
export function waitVideoSeeked(
  video: HTMLVideoElement,
  timeSec: number,
  timeoutMs = 2500,
): Promise<void> {
  return new Promise((resolve) => {
    const d = video.duration
    const hasDur = Number.isFinite(d) && d > 0
    const t = hasDur ? Math.min(Math.max(0, timeSec), Math.max(0, d - 1e-3)) : Math.max(0, timeSec)
    if (!Number.isFinite(t)) {
      resolve()
      return
    }
    if (Math.abs(video.currentTime - t) < 1e-4) {
      resolve()
      return
    }
    let settled = false
    const finish = () => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      video.removeEventListener('seeked', onSeeked)
      video.removeEventListener('error', onError)
      resolve()
    }
    const onSeeked = () => finish()
    const onError = () => finish()
    const timer = globalThis.setTimeout(finish, timeoutMs)
    video.addEventListener('seeked', onSeeked, { once: true })
    video.addEventListener('error', onError, { once: true })
    try {
      video.currentTime = t
    } catch {
      finish()
    }
  })
}

/**
 * 在 `video.videoWidth` / `video.videoHeight` 均大于 0 后再执行回调。
 * `loadeddata` 之后部分浏览器仍有一两帧为 0，此时创建 `THREE.VideoTexture` 会在上传时触发
 * `WebGL: INVALID_VALUE: texImage2D: no video`。
 *
 * @param video - 已设置 `src` 的 `<video>`
 * @param fn - 可安全创建 `VideoTexture` 时调用（仅调用一次）
 * @param options - `timeoutMs`：放弃轮询前等待毫秒数；`onTimeout`：超时仍未获得可绘制尺寸时回调（如提示换 H.264）
 */
export type RunWhenVideoDrawableOptions = {
  timeoutMs?: number
  onTimeout?: () => void
}

export function runWhenVideoHasDrawableDimensions(
  video: HTMLVideoElement,
  fn: () => void,
  options?: RunWhenVideoDrawableOptions,
): void {
  const timeoutMs = options?.timeoutMs ?? 10000
  const onTimeout = options?.onTimeout
  let ran = false
  let decodeKickTried = false
  let timeoutReported = false
  const run = () => {
    if (ran) return
    ran = true
    fn()
  }
  const t0 = globalThis.performance?.now?.() ?? 0
  const poll = () => {
    if (video.videoWidth > 0 && video.videoHeight > 0) {
      run()
      return
    }
    const elapsed = (globalThis.performance?.now?.() ?? 0) - t0
    if (!decodeKickTried && elapsed > 250) {
      decodeKickTried = true
      void globalThis.Promise.resolve(video.play())
        .then(() => {
          try {
            video.pause()
          } catch {
            /* ignore */
          }
        })
        .catch(() => {})
    }
    if (elapsed > timeoutMs) {
      if (!ran && !timeoutReported) {
        timeoutReported = true
        onTimeout?.()
      }
      return
    }
    globalThis.requestAnimationFrame(poll)
  }
  globalThis.requestAnimationFrame(poll)
}

/**
 * 计算当前全局时间对应的视频内时间（秒）：与全局时间轴一致（自动播放/拖拽同步），不使用偏移。
 *
 * @param _layer - 图层（保留参数以兼容调用方；`screenVideoTimelineOffsetSec` 不再参与计算）
 * @param globalTimeSec - 编辑器全局时间轴秒数
 */
export function resolveVideoTimeForLayer(_layer: AnimationLayer, globalTimeSec: number): number {
  const g = Number(globalTimeSec)
  if (!Number.isFinite(g)) return 0
  return Math.max(0, g)
}

/**
 * 对屏幕 mesh 上所有标准/物理材质单元绑定同一漫反射与自发光贴图槽。
 *
 * @param screenMesh - 屏幕网格
 * @param tex - 已配置色彩空间的纹理
 */
export function bindMapAndEmissiveToScreenMesh(screenMesh: THREE.Mesh, tex: THREE.Texture): void {
  const mats: THREE.Material[] = Array.isArray(screenMesh.material)
    ? [...screenMesh.material]
    : [screenMesh.material]
  for (const m of mats) {
    if (m instanceof THREE.MeshStandardMaterial) {
      m.map = null
      m.emissiveMap = tex
      m.color.setHex(0x000000)
      m.emissive.setHex(0xffffff)
      m.emissiveIntensity = 1
      m.toneMapped = false
      m.roughness = 1
      m.metalness = 0
      m.envMapIntensity = 0
      m.needsUpdate = true
    }
  }
}

/**
 * 清除屏幕 mesh 上由本模块写入的贴图槽并恢复默认底色（与视口占位屏一致）。
 *
 * @param screenMesh - 屏幕网格
 */
export function clearScreenMeshMaps(screenMesh: THREE.Mesh): void {
  const mats: THREE.Material[] = Array.isArray(screenMesh.material)
    ? [...screenMesh.material]
    : [screenMesh.material]
  for (const m of mats) {
    if (m instanceof THREE.MeshStandardMaterial) {
      m.map = null
      m.emissiveMap = null
      m.emissive.setHex(0x000000)
      m.emissiveIntensity = 1
      m.toneMapped = false
      m.roughness = 1
      m.metalness = 0
      m.envMapIntensity = 0
      m.color.setHex(0x1a1d22)
      m.needsUpdate = true
    }
  }
}
